<template>
  <v-container fluid>
    <v-card>
      <v-card-subtitle> Attendance </v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-radio-group
              row
              v-model="dateSelectionMode"
              label="Date selection mode"
              @change="loadReport"
            >
              <v-radio value="single" label="Single date" />
              <v-radio value="range" label="Date Range" />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field @blur="loadReport" @keyup.enter="loadReport" label="Course Name" v-model="courseName"/>
          </v-col>
        </v-row>
        <v-row v-if="dateSelectionMode == 'single'">
          <v-col cols="12">
            <v-text-field
              v-model="endDate"
              label="Report Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.required]"
              @change="loadReport"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.required]"
              @change="loadReport"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.dateRange(startDate, endDate)]"
              @change="loadReport"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-sheet
      v-if="report"
      class="d-flex justify-space-between mt-10"
    >
      <div v-if="unmatchedMeetings && unmatchedMeetings.length > 0">
        <v-alert type="warning" border="left" :colored-border="true">
          <a @click="unmatchedMeetingDialog = true">{{unmatchedMeetings.length}} unassigned meetings found</a>
        </v-alert>
      </div>
      <v-btn v-if="report.attendances.length > 0" @click="print('attendance-report')">Print</v-btn>
    </v-sheet>

    <v-card id="attendance-report" class="mt-10" v-if="report">
      <div
        v-if="dateSelectionMode == 'single'"
        class="d-flex justify-space-between pa-5"
      >
        <v-btn elevation="2" @click="changeSingleDate(-1)" icon
          ><v-icon>mdi-arrow-left</v-icon></v-btn
        >
        <div class="text-h6">{{ endDate | formatDate }}</div>
        <v-btn elevation="2" @click="changeSingleDate(1)" icon
          ><v-icon>mdi-arrow-right</v-icon></v-btn
        >
      </div>
      <div v-else class="d-flex justify-center pa-5">
        <div class="text-h6">
          {{ startDate | formatDate }} - {{ endDate | formatDate }}
        </div>
      </div>
      <v-card-title class="justify-center"> Attendance Records </v-card-title>
      <v-card-subtitle style="text-align: center">
        Found {{ report.attendances.length }} courses scheduled
      </v-card-subtitle>
      <v-card-text>
        <v-data-table
          v-if="report"
          :headers="headers"
          :items="report.attendances"
          :loading="isAppLoading"
          :items-per-page="100"
          item-key="id"
          class="elevation-1"
          :sort-by="['startTime']"
          :sort-desc="[false]"
          :footer-props="{
            itemsPerPageOptions: [25, 50, 100, 500],
          }"
        >
          <template v-slot:item.courseName="{ item }">
            <a @click="showSchedule(item.date, item.courseScheduleId)">
              {{ item.courseName }}
            </a>
          </template>
          <template v-slot:item.startTime="{ item }">
            {{ item.startTime | formatTime }} - {{ item.endTime | formatTime
            }}<br />
            {{ item.date | formatDate }}
          </template>
          <template v-slot:item.maxDurationInSeconds="{ item }">
            {{ timelength(item.maxDurationInSeconds) }}
          </template>
          <template v-slot:item.uniqueAttendees="{ item }">
            <v-chip
              class="mx-1 my-3"
              v-for="(a, index) in item.uniqueAttendees"
              :key="index"
              >{{ a }}</v-chip
            >
          </template>
        </v-data-table>

        <v-overlay :absolute="true" :value="overlay" class="text-center">
          <v-progress-circular
            :width="10"
            :size="100"
            color="red"
            indeterminate
          />
          <br />
          <div class="ma-5 text-center">Loading...</div>
        </v-overlay>
      </v-card-text>
    </v-card>

    <v-dialog v-if="selectedScheduleId" v-model="scheduleDialog">
      <v-sheet>
        <course-schedule
          titleColor="primary"
          :scheduleId="selectedScheduleId"
          :scheduleDate="selectedDate"
          :allowDelete="false"
          :viewCourse="true"
          :key="selectedScheduleId"
        />

        <div class="d-flex justify-center">
          <v-btn
            class="my-5"
            text
            color="secondary"
            @click="scheduleDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-sheet>
    </v-dialog>

    <v-dialog v-model="unmatchedMeetingDialog">
      <v-sheet class="pa-1 pa-lg-5">
        <v-card class="my-2" v-for="meet in unmatchedMeetings" :key="meet.id">
          <v-card-title>Organized by {{meet.records[0].organizerEmail}} 
            (Meeting Code: 
              <span class="mx-2">
                <a @click="viewGoogleCalendarEvent(meet.records[0])">
                  {{`${meet.meetingCode.toLowerCase()}`}}
                </a>
              </span>
            )
          </v-card-title>
          <v-card-text>
            <v-row v-for="record in meet.records" :key="record.id">
              <v-col cols="4">{{record.displayName}}</v-col>
              <v-col cols="4">
                {{record.localDate | formatDate}}
                {{record.localTime | formatTime}}
              </v-col>
              <v-col cols="4">
                {{ timelength(record.durationSeconds) }} on {{record.deviceType}}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <div class="d-flex justify-center">
          <v-btn
            class="my-5"
            text
            color="secondary"
            @click="unmatchedMeetingDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-sheet>
    </v-dialog>

    <v-dialog v-if="selectedMeetingRecord" v-model="googleCalendarEventDialog">
      <v-card>
        <v-card-title>
          <v-spacer />
          <v-btn @click="googleCalendarEventDialog = false" icon>
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <course-schedule-google-event
            :key="`${selectedMeetingRecord.id}`"
            :eventId="selectedMeetingRecord.calendarEventId"
            :accountOwner="selectedMeetingRecord.organizerEmail"
          />
        </v-card-text>
        <v-divider />
        <div class="d-flex justify-center">
          <v-btn
            class="mb-5"
            text
            color="secondary"
            @click="googleCalendarEventDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/store/util/rules.js";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
const CourseSchedule = () => import("@/components/course/CourseSchedule.vue");
const CourseScheduleGoogleEvent = () =>
  import("@/components/google/CourseScheduleGoogleEvent.vue");

export default {
  data() {
    return {
      overlay: true,
      schedules: [],
      courses: [],
      printing: false,
      rules,
      report: null,
      unmatchedMeetings: null,
      unmatchedMeetingDialog: false,
      selectedScheduleId: null,
      scheduleDialog: false,
      selectedDate: null,
      courseName: null,
      googleCalendarEventDialog: false,
      selectedMeetingRecord: null,
      headers: [
        {
          text: "Course",
          value: "courseName",
          align: "start",
          sortable: true,
          width: "300px",
          fixed: true,
        },
        {
          text: "Date/Time",
          value: "startTime",
          align: "start",
          sortable: true,
          width: "200px",
        },
        {
          text: "Duration",
          value: "maxDurationInSeconds",
          align: "start",
          sortable: true,
          width: "150px",
        },
        {
          text: "Attendees",
          value: "uniqueAttendees",
          align: "start",
          sortable: true,
        },
      ],
    };
  },
  filters: {
    formatTime(time) {
      return moment(time, "HH:mm").format("h:mm a");
    },
    formatDate(date) {
      return moment(date).format("ddd, MMM DD, yyyy");
    },
    formatDateTime(date) {
      return moment.utc(date).local().format("ddd, MMM DD, YYYY h:mm a");
    },
    formatDecimal(val) {
      if (val) {
        return val.toFixed(2);
      } else {
        return 0;
      }
    },
  },
  computed: {
    ...mapGetters(["currentUser", "isAppLoading", "globalCenter", "myCenters"]),
    ...mapGetters("reports", [
      "reportStartDate",
      "reportEndDate",
      "reportDateSelectionMode",
      "selectedPanel",
    ]),
    dateSelectionMode: {
      get() {
        return this.reportDateSelectionMode;
      },
      set(value) {
        this.setReportDateSelectionMode(value);
      },
    },
    startDate: {
      get() {
        return this.reportStartDate;
      },
      set(value) {
        this.setReportStartDate(value);
      },
    },
    endDate: {
      get() {
        return this.reportEndDate;
      },
      set(value) {
        this.setReportEndDate(value);
      },
    },
    selectedExpandPanel: {
      get() {
        return this.selectedPanel;
      },
      set(value) {
        this.setSelectedPanel(value);
      },
    },
  },
  mounted() {
    if (this.startDate == null) {
      this.startDate = moment().startOf("month").format("YYYY-MM-DD");
    }

    if (this.endDate == null) {
      this.endDate = moment().format("YYYY-MM-DD");
    }

    this.loadReport();
  },
  methods: {
    ...mapActions(["addErrorMessage", "clearMessage"]),
    ...mapActions("reports", [
      "setReportDateSelectionMode",
      "setReportEndDate",
      "setReportStartDate",
      "setSelectedPanel",
    ]),

    async loadReport() {
      let start = this.startDate;
      const end = this.endDate;

      if (this.dateSelectionMode == "single") {
        start = end;
      } else {
        start = this.startDate;
      }

      const params = {
        start_date: start,
        end_date: end,
        course_name: this.courseName,
      };

      this.overlay = true;

      this.loadScheduleFromServer(params);
    },
    async loadScheduleFromServer(params) {
      return this.$axios("/report/course-attendance", { params }).then(
        (resp) => {
          this.report = resp.data.data;
          const groupedByMeetCode = this.groupBy(this.report.unmatchedMeetingRecords, 'meetingCode');
          const meetingCodes = Object.keys(groupedByMeetCode);
          if(meetingCodes && meetingCodes.length > 0){
            this.unmatchedMeetings = meetingCodes.map(mc => {
              return {
                meetingCode: mc,
                records: groupedByMeetCode[mc]
              }
            })
          }
          this.overlay = false;
        }
      );
    },
    showSchedule(date, scheduleId) {
      this.selectedDate = date;
      this.selectedScheduleId = scheduleId;
      this.scheduleDialog = true;
    },
    changeSingleDate(change) {
      this.endDate = moment(this.endDate)
        .add(change, "days")
        .format("YYYY-MM-DD");
      this.loadReport();
    },
    timelength(seconds) {
      if (seconds) {
        const hrMinSec = new Date(seconds * 1000)
          .toISOString()
          .substr(11, 8)
          .split(":")
          .map((s) => parseInt(s));
        if (hrMinSec[0] < 1 && hrMinSec[1] < 1) {
          return `${hrMinSec[2] > 0 ? hrMinSec[2] + " seconds" : "N/A"}`;
        } else {
          return `${hrMinSec[0] > 0 ? hrMinSec[0] + " hr" : ""} ${
            hrMinSec[1] > 0 ? hrMinSec[1] + " mins" : ""
          }`;
        }
      } else {
        return "-";
      }
    },
    viewGoogleCalendarEvent(record){
      this.selectedMeetingRecord = record;
      this.googleCalendarEventDialog = true;
    },
    groupBy(items, key) {
      return items.reduce(
        (result, item) => ({
          ...result,
          [item[key]]: [...(result[item[key]] || []), item],
        }),
        {}
      );
    },
    print(componentId) {
      this.printing = true;

      // console.log(`printing component: ${componentId}`)
      this.$htmlToPaper(componentId, null, () => {
        this.printing = false;
      });
    },
  },
  components: {
    CourseSchedule,
    CourseScheduleGoogleEvent
  },
};
</script>